import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useMarcarSprintEspera, useMarcarSprintHito, useSprintsCatalogo } from '@/hooks/useObras';
import { useActualizarSubPartida, useSubPartidas } from '@/hooks/useSubPartidas';
import { cn } from '@/lib/utils';
import {
  HITOS_DESEMBOLSO,
  type HitoDesembolsoCodigo,
  type SubPartidaListado,
} from '@adelante/shared';
import { CaretDown, CaretRight, CircleNotch, Pause } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

/**
 * Admin · Sprints — vista agrupada por sprint: qué sub-partidas participan en
 * cada uno. Permite reasignar una sub-partida a otro sprint (cambia su
 * sprint_numero) sin entrar al detalle. Colapsable por sprint.
 */
export function SprintsPantalla() {
  const subsQ = useSubPartidas({ activo: true });
  const catalogoQ = useSprintsCatalogo();
  const actualizar = useActualizarSubPartida();
  const marcarEspera = useMarcarSprintEspera();
  const marcarHito = useMarcarSprintHito();

  const [q, setQ] = useState('');
  const [colapsados, setColapsados] = useState<Set<number>>(new Set());

  const subs = subsQ.data?.data ?? [];
  const catalogo = catalogoQ.data?.data ?? [];

  // Catálogo: numero_global → {nombre, es_espera, hito_desembolso}. Y lista de sprints válidos.
  const infoSprint = useMemo(() => {
    const m = new Map<
      number,
      { nombre: string; es_espera: boolean; hito_desembolso: HitoDesembolsoCodigo | null }
    >();
    for (const c of catalogo)
      m.set(c.numero_global, {
        nombre: c.nombre,
        es_espera: c.es_espera,
        hito_desembolso: c.hito_desembolso,
      });
    return m;
  }, [catalogo]);

  const sprintsValidos = useMemo(() => {
    const set = new Set<number>(catalogo.map((c) => c.numero_global));
    for (const s of subs) set.add(s.sprint_numero);
    return [...set].sort((a, b) => a - b);
  }, [catalogo, subs]);

  // Agrupar sub-partidas por sprint (respetando el filtro de texto).
  const grupos = useMemo(() => {
    const t = q.trim().toLowerCase();
    const filtradas = t
      ? subs.filter((s) =>
          `${s.codigo} ${s.nombre} ${s.partida_codigo} ${s.partida_nombre}`
            .toLowerCase()
            .includes(t),
        )
      : subs;
    const m = new Map<number, SubPartidaListado[]>();
    for (const s of filtradas) {
      const arr = m.get(s.sprint_numero) ?? [];
      arr.push(s);
      m.set(s.sprint_numero, arr);
    }
    // Sprints de espera del catálogo: mostrarlos aunque NO tengan sub-partidas
    // (un sprint de espera no debería tenerlas, pero debe verse para gestionarlo).
    // Solo cuando no hay filtro de texto.
    if (!t) {
      for (const c of catalogo) {
        if (c.es_espera && !m.has(c.numero_global)) m.set(c.numero_global, []);
      }
    }
    return [...m.entries()]
      .sort(([a], [b]) => a - b)
      .map(([sprint, lista]) => ({
        sprint,
        nombre: infoSprint.get(sprint)?.nombre ?? `Sprint ${sprint}`,
        es_espera: infoSprint.get(sprint)?.es_espera ?? false,
        hito_desembolso: infoSprint.get(sprint)?.hito_desembolso ?? null,
        sub_partidas: lista.sort((x, y) =>
          x.codigo.localeCompare(y.codigo, 'es', { numeric: true }),
        ),
      }));
  }, [subs, q, infoSprint, catalogo]);

  function toggleEspera(sprint: number, es_espera: boolean, numSubs = 0) {
    // Un sprint de espera no puede tener sub-partidas. Si se intenta marcar uno
    // que las tiene, se bloquea (hay que moverlas a otro sprint primero).
    if (es_espera && numSubs > 0) {
      toast.error('No se puede marcar como espera', {
        description: `El Sprint ${sprint} tiene ${numSubs} sub-partida${numSubs === 1 ? '' : 's'}. Un sprint de espera no puede tenerlas — movelas a otro sprint primero.`,
      });
      return;
    }
    marcarEspera.mutate(
      { numero: sprint, es_espera },
      {
        onSuccess: () =>
          toast.success(`Sprint ${sprint}: ${es_espera ? 'marcado' : 'desmarcado'} como espera`),
        onError: (e) =>
          toast.error('No se pudo actualizar', {
            description: e instanceof Error ? e.message : 'Error',
          }),
      },
    );
  }

  function cambiarHito(sprint: number, hito: HitoDesembolsoCodigo | null) {
    marcarHito.mutate(
      { numero: sprint, hito_desembolso: hito },
      {
        onSuccess: () =>
          toast.success(
            hito
              ? `Sprint ${sprint}: hito de desembolso → ${HITOS_DESEMBOLSO.find((h) => h.codigo === hito)?.nombre}`
              : `Sprint ${sprint}: sin hito de desembolso`,
          ),
        onError: (e) =>
          toast.error('No se pudo actualizar', {
            description: e instanceof Error ? e.message : 'Error',
          }),
      },
    );
  }

  function toggle(s: number) {
    setColapsados((prev) => {
      const n = new Set(prev);
      if (n.has(s)) n.delete(s);
      else n.add(s);
      return n;
    });
  }

  function moverA(sp: SubPartidaListado, nuevoSprint: number) {
    if (nuevoSprint === sp.sprint_numero) return;
    actualizar.mutate(
      { id: sp.id, parche: { sprint_numero: nuevoSprint } },
      {
        onSuccess: () => toast.success(`${sp.codigo} → Sprint ${nuevoSprint}`),
        onError: (e) =>
          toast.error('No se pudo mover', {
            description: e instanceof Error ? e.message : 'Error',
          }),
      },
    );
  }

  const cargando = subsQ.isLoading || catalogoQ.isLoading;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Sprints</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Qué sub-partidas participan en cada sprint. Reasigná una al sprint que corresponda con
            el selector.
          </p>
        </div>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar sub-partida…"
          className="h-9 w-64"
        />
      </div>

      {cargando && (
        <div className="flex items-center gap-2 py-8 text-muted-foreground">
          <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
          Cargando…
        </div>
      )}

      {!cargando &&
        grupos.map((g) => {
          const colapsado = colapsados.has(g.sprint);
          const hitoInfo = g.hito_desembolso
            ? HITOS_DESEMBOLSO.find((h) => h.codigo === g.hito_desembolso)
            : null;
          return (
            <Card
              key={g.sprint}
              className="overflow-hidden"
              style={hitoInfo ? { borderLeft: `4px solid ${hitoInfo.color}` } : undefined}
            >
              <div
                className={cn(
                  'flex w-full items-center justify-between gap-3 border-b border-border px-4 py-2',
                  g.es_espera ? 'bg-violet-50' : 'bg-zinc-50',
                )}
              >
                <button
                  type="button"
                  onClick={() => toggle(g.sprint)}
                  className="flex flex-1 items-center gap-2 text-left text-sm font-semibold hover:opacity-80"
                >
                  {colapsado ? (
                    <CaretRight className="h-4 w-4" weight="bold" aria-hidden="true" />
                  ) : (
                    <CaretDown className="h-4 w-4" weight="bold" aria-hidden="true" />
                  )}
                  <span className="font-mono">Sprint {g.sprint}</span>
                  <span className="text-muted-foreground">· {g.nombre}</span>
                </button>
                <div className="flex shrink-0 items-center gap-4">
                  <Select
                    value={g.hito_desembolso ?? '__ninguno__'}
                    onValueChange={(v) =>
                      cambiarHito(
                        g.sprint,
                        v === '__ninguno__' ? null : (v as HitoDesembolsoCodigo),
                      )
                    }
                  >
                    <SelectTrigger
                      className="h-7 w-40 text-xs"
                      title="Hito de desembolso que dispara este sprint al completarse"
                    >
                      <SelectValue placeholder="Sin hito" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__ninguno__">Sin hito de desembolso</SelectItem>
                      {HITOS_DESEMBOLSO.map((h) => (
                        <SelectItem key={h.codigo} value={h.codigo}>
                          <span className="flex items-center gap-1.5">
                            <span
                              className="inline-block h-2 w-2 rounded-full"
                              style={{ backgroundColor: h.color }}
                              aria-hidden="true"
                            />
                            {h.nombre}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <label
                    className={cn(
                      'flex cursor-pointer select-none items-center gap-1.5 text-xs font-medium',
                      g.es_espera ? 'text-violet-700' : 'text-muted-foreground',
                    )}
                    title="Marcar este sprint como de espera (colado/curado)"
                  >
                    <input
                      type="checkbox"
                      checked={g.es_espera}
                      disabled={marcarEspera.isPending}
                      onChange={(e) =>
                        toggleEspera(g.sprint, e.target.checked, g.sub_partidas.length)
                      }
                      className="h-4 w-4 accent-violet-500"
                    />
                    <Pause className="h-3 w-3" weight="fill" aria-hidden="true" />
                    De espera
                  </label>
                  <span className="text-xs font-normal text-muted-foreground">
                    {g.sub_partidas.length} sub-partidas
                  </span>
                </div>
              </div>

              {!colapsado && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-mono text-xs">Código</TableHead>
                      <TableHead>Sub-partida</TableHead>
                      <TableHead>Partida</TableHead>
                      <TableHead className="text-center">Crítica</TableHead>
                      <TableHead className="text-right">Mover a sprint</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {g.sub_partidas.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="py-3 text-center text-xs text-muted-foreground"
                        >
                          {g.es_espera
                            ? 'Sprint de espera — sin sub-partidas (correcto).'
                            : 'Sin sub-partidas.'}
                        </TableCell>
                      </TableRow>
                    )}
                    {g.sub_partidas.map((sp) => (
                      <TableRow key={sp.id}>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {sp.codigo}
                        </TableCell>
                        <TableCell className="text-sm">{sp.nombre}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {sp.partida_codigo} {sp.partida_nombre}
                        </TableCell>
                        <TableCell className="text-center">
                          {sp.es_critica && (
                            <Badge className="bg-amber-100 text-amber-700">crítica</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Select
                            value={String(sp.sprint_numero)}
                            onValueChange={(v) => moverA(sp, Number(v))}
                          >
                            <SelectTrigger className="ml-auto h-8 w-28">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {sprintsValidos.map((n) => {
                                const esEspera = infoSprint.get(n)?.es_espera ?? false;
                                return (
                                  <SelectItem
                                    key={n}
                                    value={String(n)}
                                    // No se puede mover una sub a un sprint de espera
                                    // (salvo que ya esté ahí, para que el valor renderice).
                                    disabled={esEspera && n !== sp.sprint_numero}
                                  >
                                    Sprint {n}
                                    {esEspera ? ' · espera' : ''}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          );
        })}

      {!cargando && grupos.length === 0 && (
        <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No hay sub-partidas{q ? ' que coincidan con la búsqueda' : ''}.
        </p>
      )}
    </div>
  );
}
